import React from 'react';
import {
  YesNoQuestion,
  QuestionAnswer,
  QuestionInterface
} from './questions/QuestionTypes';

// We need to create new question types for Number inputs
interface NumberQuestionProps {
  id: string;
  question: string;
  value: QuestionAnswer;
  onChange: (value: QuestionAnswer) => void;
  required?: boolean;
  error?: string;
}

const NumberQuestionComponent: React.FC<NumberQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="number"
      className="w-32 input"
      min="0"
      value={value.answer || ''}
      onChange={(e) => onChange({ ...value, answer: e.target.value })}
    />
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

// Yes/No with Time Duration component for question 14a
const YesNoTimeDurationQuestion: React.FC<NumberQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex mb-2 space-x-4">
      {['Yes', 'No'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {value.answer === 'Yes' && (
      <div>
        <label className="text-sm label">How long did it take to resolve? (minutes)</label>
        <input
          type="number"
          className="w-32 input"
          min="0"
          placeholder="Minutes"
          value={value.details || ''}
          onChange={(e) => onChange({ ...value, details: e.target.value })}
        />
      </div>
    )}
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

interface Question4Data {
  [questionId: string]: QuestionAnswer;
}

interface Step3Question4Props {
  data: Question4Data;
  onChange: (data: Question4Data) => void;
  errors: Record<string, string>;
  selectedTimeline?: string;
  questionNumber?: number;
  questionTitle?: string;
  questionDescription?: string;
  currentSection?: number;
  totalSections?: number;
}

// Timeline-based questions for Question 4 (Materials and Equipment)
const TIMELINE_QUESTIONS: Record<string, QuestionInterface[]> = {
  morning: [
    // 4a
    { id: '4a', question: 'The ballot paper packages were sealed and was opened in the presence of all Officials', type: 'yes_no', required: true },
    // 4b
    { id: '4b', question: 'Number of ballot papers on opening of Polling Centre', type: 'number', required: true },
    // 4c
    { id: '4c', question: 'Number of ballot boxes on opening of Polling Centre', type: 'number', required: true },
    // 4d
    { id: '4d', question: 'Number of embossing devices on opening of Polling Centre', type: 'number', required: true },
    // 4e
    { id: '4e', question: 'Number of Ink bottles for marking fingers on opening of Polling Centre', type: 'number', required: true },
    // 4f
    { id: '4f', question: 'Number of pens on opening of Polling Centre', type: 'number', required: true },
    // 4g
    { id: '4g', question: 'How many Voters List/Register were available at the Polling Centre at the time of opening?', type: 'number', required: true },
    // 4h
    { id: '4h', question: 'Have you witnessed or heard of any malfunctions of polling equipment or shortage of voting materials that caused voting to be negatively affected? How long did it take to resolve the issue?', type: 'yes_no_time_duration', required: true },
  ],
  afternoon: [
    // 13a
    { id: '13a', question: 'Number of additional ballot boxes received at polling Centre', type: 'number', required: true },
    // 13b
    { id: '13b', question: 'Number of embossing devices at Polling Centre', type: 'number', required: true },
    // 13c
    { id: '13c', question: 'Number of Ink bottles for marking fingers at Polling Centre', type: 'number', required: true },
    // 14a
    { id: '14a', question: 'Have you witnessed or heard of any malfunctions of polling equipment or shortage of voting materials that caused voting to be negatively affected? How long did it take to resolve the issue?', type: 'yes_no_time_duration', required: true },
  ],
  vote_count: [
    // No equipment questions for vote count timeline
  ]
};

const QUESTION_COMPONENTS = {
  'yes_no': YesNoQuestion,
  'number': NumberQuestionComponent,
  'yes_no_time_duration': YesNoTimeDurationQuestion,
};

const Step3Question4: React.FC<Step3Question4Props> = ({ data, onChange, errors, selectedTimeline }) => {
  const updateQuestion = (questionId: string, value: QuestionAnswer) => {
    onChange({
      ...data,
      [questionId]: value
    });
  };

  // Get questions for the selected timeline with conditional filtering
  const getQuestionsForTimeline = () => {
    if (!selectedTimeline || !TIMELINE_QUESTIONS[selectedTimeline as keyof typeof TIMELINE_QUESTIONS]) {
      return [];
    }

    const allQuestions = TIMELINE_QUESTIONS[selectedTimeline as keyof typeof TIMELINE_QUESTIONS];

    // Filter out conditional questions that shouldn't be shown
    return allQuestions.filter(question => {
      if (!question.conditional) return true;

      const dependentAnswer = data?.[question.conditional.dependsOn]?.answer;
      return dependentAnswer === question.conditional.showWhen;
    });
  };

  const questions = getQuestionsForTimeline();

  const getCompletionStatus = () => {
    const completed = questions.filter(q => data?.[q.id]?.answer).length;
    return { completed, total: questions.length };
  };

  const { completed, total } = getCompletionStatus();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="mb-4 text-xl font-semibold text-gray-900">
          Question 4: Equipment & Materials
        </h2>
        <p className="mb-4 text-gray-600">
          Timeline: {selectedTimeline ? selectedTimeline.charAt(0).toUpperCase() + selectedTimeline.slice(1).replace('_', ' ') : 'No timeline selected'}
        </p>
        {!selectedTimeline && (
          <div className="p-4 mb-4 border border-yellow-200 rounded-lg bg-yellow-50">
            <p className="text-yellow-800">Please select a timeline in Step 1 to see relevant questions for Question 4.</p>
          </div>
        )}

        {/* Progress indicator */}
        <div className="p-3 mb-6 border border-purple-200 rounded-lg bg-purple-50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-purple-900">Question 4 Progress</span>
            <span className="text-sm text-purple-800">{completed}/{total} completed</span>
          </div>
          <div className="h-2 mt-2 bg-purple-200 rounded-full">
            <div
              className="h-2 transition-all duration-300 bg-purple-600 rounded-full"
              style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {questions.length > 0 ? questions.map((question) => {
          const QuestionComponent = QUESTION_COMPONENTS[question.type as keyof typeof QUESTION_COMPONENTS];
          const questionValue = data?.[question.id] || {};
          const questionError = errors?.[question.id];

          return (
            <div key={question.id} className="p-4 transition-colors bg-white border border-gray-200 rounded-lg hover:border-purple-300">
              <QuestionComponent
                id={question.id}
                question={`${question.id}: ${question.question}`}
                value={questionValue}
                onChange={(value) => updateQuestion(question.id, value)}
                required={question.required}
                error={questionError}
              />
            </div>
          );
        }) : (
          <div className="py-8 text-center text-gray-500">
            {selectedTimeline ? 'No questions available for this timeline.' : 'Select a timeline to view questions.'}
          </div>
        )}
      </div>

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="p-4 border border-red-200 rounded-lg bg-red-50">
          <h4 className="mb-2 font-medium text-red-900">Please complete the following required questions:</h4>
          <ul className="space-y-1 text-sm text-red-800">
            {Object.entries(errors).map(([questionId, error]) => (
              <li key={questionId}>• {questionId}: {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Period Summary */}
      <div className="p-4 rounded-lg bg-gray-50">
        <h4 className="mb-2 font-medium text-gray-900">Summary</h4>
        <p className="text-sm text-gray-700">
          This section covers equipment and materials at the polling station including ballot papers,
          ballot boxes, and other election materials.
        </p>
        <div className="mt-2 text-sm text-gray-600">
          Completion: {completed}/{total} questions ({total > 0 ? Math.round((completed / total) * 100) : 0}%)
        </div>
      </div>
    </div>
  );
};

export default Step3Question4;